import React from "react";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Plus } from "lucide-react";
import { businessApi } from "../../lib/api";

export default function BusinessClaimsPage() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showCreate, setShowCreate] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try { const { data } = await businessApi().get("/b2b/2r5b/claims"); setItems(data.items || []); }
    catch { toast.error("Fejl"); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-4" data-testid="business-claims-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark">Claims & Reklamationer</h1>
          <p className="text-sm text-brand-text2">Indsend reklamation, upload dokumentation og følg status.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-3 py-2 bg-brand-red text-white rounded text-sm font-semibold flex items-center gap-1" data-testid="claim-new-btn">
          <Plus size={14}/> Ny claim
        </button>
      </div>
      {loading ? <Loader2 className="animate-spin" size={16}/> : items.length === 0 ? (
        <div className="rounded-lg border border-brand p-6 text-center text-brand-text2">Ingen claims endnu</div>
      ) : (
        <div className="rounded-lg border border-brand bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="claims-table">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">Tracking</th><th className="px-3 py-2 text-right">Beløb</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Indsendt</th></tr>
            </thead>
            <tbody>{items.map((c) => (
              <tr key={c.id} className="border-t border-brand" data-testid={`claim-row-${c.id}`}>
                <td className="px-3 py-2">{c.claim_type}</td>
                <td className="px-3 py-2 font-mono text-xs">{c.tracking_number || "—"}</td>
                <td className="px-3 py-2 text-right">{c.claim_amount_dkk ? `${c.claim_amount_dkk.toFixed(2)} DKK` : "—"}</td>
                <td className="px-3 py-2"><span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{c.status}</span></td>
                <td className="px-3 py-2 text-xs">{(c.submitted_at_iso || "").slice(0, 10)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {showCreate && <NewClaimModal onClose={() => { setShowCreate(false); refresh(); }}/>}
    </div>
  );
}

function NewClaimModal({ onClose }) {
  const [form, setForm] = React.useState({ claim_type: "damaged_package", tracking_number: "", customer_message: "", claim_amount_dkk: "" });
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if ((form.customer_message || "").length < 4) return toast.error("Beskriv claim (min 4 tegn)");
    setBusy(true);
    try {
      await businessApi().post("/b2b/2r5b/claims", {
        claim_type: form.claim_type,
        tracking_number: form.tracking_number || null,
        customer_message: form.customer_message,
        claim_amount_dkk: form.claim_amount_dkk ? parseFloat(form.claim_amount_dkk) : null,
      });
      toast.success("Claim indsendt");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="new-claim-modal">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full">
        <h3 className="text-lg font-bold mb-3">Ny claim</h3>
        <div className="space-y-3">
          <div><label className="text-xs text-brand-text2">Type</label>
            <select className="input-flat w-full" value={form.claim_type} onChange={(e) => setForm({ ...form, claim_type: e.target.value })} data-testid="claim-type">
              {["damaged_package","lost_package","delayed_delivery","wrong_delivery","missing_POD","recipient_dispute","customer_dispute","failed_delivery_dispute","invoice_dispute"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select></div>
          <div><label className="text-xs text-brand-text2">Tracking (valgfri)</label>
            <input className="input-flat w-full" value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} data-testid="claim-tracking"/></div>
          <div><label className="text-xs text-brand-text2">Beskrivelse</label>
            <textarea rows={4} className="input-flat w-full" value={form.customer_message} onChange={(e) => setForm({ ...form, customer_message: e.target.value })} data-testid="claim-message"/></div>
          <div><label className="text-xs text-brand-text2">Krav DKK (valgfri)</label>
            <input type="number" className="input-flat w-full" value={form.claim_amount_dkk} onChange={(e) => setForm({ ...form, claim_amount_dkk: e.target.value })} data-testid="claim-amount"/></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded border border-brand text-sm">Annullér</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded bg-brand-red text-white text-sm font-semibold" data-testid="claim-submit">
            {busy && <Loader2 className="inline animate-spin mr-1" size={14}/>}Send
          </button>
        </div>
      </div>
    </div>
  );
}
